import { useEffect, useState, type ReactNode } from "react";
import { Button, Dialog, TextField, vars } from "@pack-it-up/ui-web";
import { useMySettings } from "../../app/useMySettings.js";

// Edits the signed-in user's display name (users/{uid}.displayName). The
// `onUserProfileWrite` trigger fans the new name out to friends and trip members.
export const EditNameModal = ({
  open,
  currentName,
  onClose,
}: {
  open: boolean;
  currentName: string;
  onClose: () => void;
}): ReactNode => {
  const { save } = useMySettings();
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(currentName);
      setError(null);
      setSaving(false);
    }
  }, [open, currentName]);

  const trimmed = name.trim();
  const valid = trimmed !== "" && trimmed !== currentName;

  const onSubmit = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    const r = await save({ displayName: trimmed });
    setSaving(false);
    if (r.ok) onClose();
    else setError("Couldn't save your name. Please try again.");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Change name"
      subtitle="This is the name your friends and trip mates see."
      compact
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid || saving} onClick={() => void onSubmit()}>
            {saving ? "Saving…" : "Save name"}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: vars.space.md }}>
        <TextField label="Display name" value={name} onChange={setName} autoComplete="name" />
        {error !== null && (
          <div role="alert" style={{ fontSize: vars.font.sizeSm, color: vars.color.danger }}>
            {error}
          </div>
        )}
      </div>
    </Dialog>
  );
};
